import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Route-level code splitting. Each screen behind the sign-in is its own chunk, fetched the first time its
 * route renders; the frame, the three public pages and the assignment dialog stay in the entry, so signing
 * in never waits on a chunk and the login page downloads no screen it cannot show.
 *
 * The screens export named components, and `React.lazy` wants a module with a default export, hence the
 * `name`. Two routes naming components of one module (the finance summary and the period form) share its
 * chunk: the browser fetches and evaluates a module once, whichever route asked first.
 */
export function lazyPage<K extends string, M extends { [P in K]: ComponentType }>(
  load: () => Promise<M>,
  name: K,
): LazyExoticComponent<M[K]> {
  return lazy(() => tracked(load()).then((module) => ({ default: module[name] })));
}

/*
 * How many chunk fetches are in flight, for the top loading bar. A chunk is waiting on the server exactly as
 * a query is, and it is the only sign a navigation gives while React keeps the previous screen up for it
 * (the router moves inside a transition, so an already-shown screen is not swapped for the fallback).
 */
let pending = 0;
const listeners = new Set<() => void>();

function tracked<T>(load: Promise<T>): Promise<T> {
  // Counted a microtask later. `React.lazy` starts the fetch while it renders the route, and a store that
  // moves in the middle of a render is one React has to re-render the tree to reconcile. The queued count
  // always lands before the settle below: it was queued first, and a settled import answers no sooner.
  queueMicrotask(() => change(1));
  return load.finally(() => change(-1));
}

function change(delta: number): void {
  pending += delta;
  for (const listener of listeners) listener();
}

export function subscribeChunkLoads(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function pendingChunkLoads(): number {
  return pending;
}

/**
 * The only way out of a chunk that no longer exists: a tab left open across a deploy asks for the old hash,
 * and only a fresh document knows the new ones. A named export so a test can observe the call — jsdom
 * implements no navigation.
 */
export function reloadPage(): void {
  window.location.reload();
}
